/**
 * Authentication Error Handler Hooks
 *
 * Custom hooks for handling URL-based and event-based authentication errors.
 * Extracted from the main AuthErrorHandler component to improve separation of concerns.
 */

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { handleUrlAuthError, handleEventAuthError } from '@features/authentication/services/authErrorHandlerService'
import { SIGNIN_STEP_KEYS } from '@constants'

export function shouldSuppressAuthErrorModal(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (window.location.pathname.includes('/account/logout/callback')) return true
    if (sessionStorage.getItem('mk_logout_redirect') === '1') return true
    return false
  } catch {
    return false
  }
}

/**
 * Hook for handling URL-based authentication errors
 *
 * @param modalId - Modal ID to use
 * @param openSignInModal - Function to open sign in modal
 * @param openRegisterModal - Function to open register modal
 */
export const useUrlAuthErrorHandler = (
  modalId: string | undefined,
  openSignInModal: (modalId?: string, error?: string, startStep?: any) => void,
  openRegisterModal: (modalId?: string, startStep?: any) => void,
) => {
  const location = useLocation()
  const navigate = useNavigate()
  // Tracks the currently processed error message to prevent duplicate processing
  const processedErrorRef = useRef<string | null>(null)
  // Prevents re-processing during URL cleanup
  const cleanupInitiatedRef = useRef(false)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const loginError = searchParams.get('loginError')

    if (!loginError) {
      // Reset refs when there's no error
      if (processedErrorRef.current !== null || cleanupInitiatedRef.current) {
        processedErrorRef.current = null
        cleanupInitiatedRef.current = false
      }
      return
    }

    if (shouldSuppressAuthErrorModal()) {
      const sp = new URLSearchParams(location.search)
      sp.delete('loginError')
      sp.delete('errorType')
      const next = sp.toString()
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
      return
    }

    // Prevent infinite loop: only process if we haven't already processed this exact error
    if (processedErrorRef.current === loginError || cleanupInitiatedRef.current) {
      // If cleanup was initiated and URL is now clean, reset the flag
      if (cleanupInitiatedRef.current && !location.search.includes('loginError')) {
        cleanupInitiatedRef.current = false
      }
      return
    }

    // Mark as processed immediately
    processedErrorRef.current = loginError
    cleanupInitiatedRef.current = true

    // Route-aware modal ID detection: use 'account-login-resort-page' for resorts page, otherwise use passed modalId or default
    const isResortsPage = location.pathname.includes('/resorts')
    const resolvedModalId = isResortsPage ? 'account-login-resort-page' : modalId || 'default-register-modal'

    try {
      const decoded = decodeURIComponent(loginError)
      const stateToken = searchParams.get('stateToken')

      const result = handleUrlAuthError(decoded, resolvedModalId, stateToken || undefined)

      // Open modal FIRST before navigation to prevent glitches
      if (result.success) {
        if (result.action === 'openRegister' && result.stepName) {
          openRegisterModal(resolvedModalId, result.stepName)
        } else if (result.action === 'openSignIn' && result.errorMessage) {
          // Explicitly set startStep to SIGNIN to ensure login fields are shown
          openSignInModal(resolvedModalId, result.errorMessage, SIGNIN_STEP_KEYS.SIGNIN)
        } else {
          // Fallback: open sign in modal with decoded error
          openSignInModal(resolvedModalId, decoded, SIGNIN_STEP_KEYS.SIGNIN)
        }
      } else {
        // Fallback to sign in modal with error message
        openSignInModal(resolvedModalId, result.errorMessage || decoded, SIGNIN_STEP_KEYS.SIGNIN)
      }

      // Clear URL parameters immediately after opening modal
      searchParams.delete('loginError')
      searchParams.delete('errorType')
      const newSearch = searchParams.toString()

      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true },
      )
    } catch (error) {
      // Fallback: try with raw error value
      const stateToken = searchParams.get('stateToken')

      const result = handleUrlAuthError(loginError, resolvedModalId, stateToken || undefined)

      // Open modal FIRST before navigation
      if (result.success) {
        if (result.action === 'openRegister' && result.stepName) {
          openRegisterModal(resolvedModalId, result.stepName)
        } else if (result.action === 'openSignIn' && result.errorMessage) {
          openSignInModal(resolvedModalId, result.errorMessage, SIGNIN_STEP_KEYS.SIGNIN)
        } else {
          openSignInModal(resolvedModalId, loginError, SIGNIN_STEP_KEYS.SIGNIN)
        }
      } else {
        // Final fallback to sign in modal
        openSignInModal(resolvedModalId, loginError, SIGNIN_STEP_KEYS.SIGNIN)
      }

      // Clear URL parameters immediately after opening modal
      searchParams.delete('loginError')
      searchParams.delete('errorType')
      const newSearch = searchParams.toString()

      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true },
      )
    }
  }, [location.search, location.pathname, navigate, openSignInModal, openRegisterModal, modalId])
}

/**
 * Hook for handling event-based authentication errors
 *
 * @param modalId - Modal ID to use
 * @param openSignInModal - Function to open sign in modal
 * @param openRegisterModal - Function to open register modal
 */
export const useEventAuthErrorHandler = (
  modalId: string | undefined,
  openSignInModal: (modalId?: string, error?: string, startStep?: any) => void,
  openRegisterModal: (modalId?: string, startStep?: any) => void,
) => {
  useEffect(() => {
    const handleAuthorizationError = (event: CustomEvent) => {
      const { errorMessage, stateToken } = event.detail || {}
      const defaultModalId = modalId || 'default-login-modal'

      if (!errorMessage) {
        return
      }

      if (shouldSuppressAuthErrorModal()) {
        return
      }

      const result = handleEventAuthError(errorMessage, defaultModalId, stateToken)

      if (result.success) {
        if (result.action === 'openRegister' && result.stepName) {
          openRegisterModal(defaultModalId, result.stepName)
        } else if (result.action === 'openSignIn' && result.errorMessage) {
          // Explicitly set startStep to SIGNIN to ensure login fields are shown
          openSignInModal(defaultModalId, result.errorMessage, SIGNIN_STEP_KEYS.SIGNIN)
        }
      } else {
        // Fallback to sign in modal with error message - explicitly set startStep to SIGNIN
        openSignInModal(defaultModalId, result.errorMessage || errorMessage, SIGNIN_STEP_KEYS.SIGNIN)
      }
    }

    window.addEventListener('authorizationError', handleAuthorizationError as EventListener)

    return () => {
      window.removeEventListener('authorizationError', handleAuthorizationError as EventListener)
    }
  }, [modalId, openSignInModal, openRegisterModal])
}
